import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Statistic,
  Row,
  Col,
  Select,
  Tooltip,
  Descriptions,
} from 'antd'
import {
  CheckOutlined,
  CloseOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import type { ColumnType } from 'antd/es/table'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { permissionRequestsApi } from '@/api/permission-requests'
import type { PermissionRequest } from '@/types/permission'

dayjs.extend(relativeTime)

export const ApprovalHistory = () => {
  const [statusFilter, setStatusFilter] = useState<string>('approved')

  // Query for historical requests (approved, rejected, expired)
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['permission-requests-history', { status: statusFilter }],
    queryFn: () =>
      permissionRequestsApi.list({
        status: statusFilter,
        page: 1,
        pageSize: 50,
      }),
    refetchInterval: 60000, // Auto-refresh every 60 seconds (less frequent than approval queue)
  })

  const getStatusTag = (status: PermissionRequest['status']) => {
    const statusConfig = {
      pending: { color: 'gold', text: 'Pending' },
      approved: { color: 'green', text: 'Approved' },
      rejected: { color: 'red', text: 'Rejected' },
      expired: { color: 'default', text: 'Expired' },
    }
    const config = statusConfig[status]
    return <Tag color={config.color}>{config.text}</Tag>
  }

  const columns: ColumnType<PermissionRequest>[] = [
    {
      title: 'Processed',
      dataIndex: 'approved_at',
      key: 'approved_at',
      width: 150,
      render: (date: string) => (date ? dayjs(date).format('MMM D, YYYY HH:mm') : '—'),
      sorter: (a, b) => {
        if (!a.approved_at) return 1
        if (!b.approved_at) return -1
        return dayjs(a.approved_at).unix() - dayjs(b.approved_at).unix()
      },
      defaultSortOrder: 'descend',
      responsive: ['md'],
    },
    {
      title: 'Target User',
      dataIndex: 'target_user_email',
      key: 'target_user_email',
      width: 200,
      render: (email: string | undefined, record: PermissionRequest) => {
        const displayText = email || record.target_user_id
        return (
          <Tooltip title={displayText}>
            <span style={{ cursor: 'help' }}>{displayText}</span>
          </Tooltip>
        )
      },
      ellipsis: {
        showTitle: false,
      },
    },
    {
      title: 'Hospital',
      dataIndex: 'facility_name',
      key: 'facility_name',
      width: 180,
      render: (name: string) => (
        <Tooltip title={name}>
          <span style={{ cursor: 'help' }}>{name}</span>
        </Tooltip>
      ),
      ellipsis: {
        showTitle: false,
      },
      responsive: ['lg'],
    },
    {
      title: 'Requester',
      dataIndex: 'requester_email',
      key: 'requester_email',
      width: 200,
      render: (email: string | undefined, record: PermissionRequest) => {
        const displayText = email || record.requester_id
        return (
          <Tooltip title={displayText}>
            <span style={{ cursor: 'help' }}>{displayText}</span>
          </Tooltip>
        )
      },
      ellipsis: {
        showTitle: false,
      },
      responsive: ['md'],
    },
    {
      title: 'Processed By',
      dataIndex: 'approved_by_email',
      key: 'approved_by_email',
      width: 200,
      render: (email: string | null) => {
        if (!email) return <span style={{ color: '#999' }}>—</span>
        return (
          <Tooltip title={email}>
            <span style={{ cursor: 'help' }}>{email}</span>
          </Tooltip>
        )
      },
      ellipsis: {
        showTitle: false,
      },
      responsive: ['lg'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: PermissionRequest['status']) => getStatusTag(status),
      responsive: ['sm'],
    },
    {
      title: 'Requested',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => dayjs(date).format('MMM D, YYYY HH:mm'),
      responsive: ['xl'],
    },
  ]

  const approvedCount = data?.data?.requests?.filter((r) => r.status === 'approved').length || 0
  const rejectedCount = data?.data?.requests?.filter((r) => r.status === 'rejected').length || 0
  const expiredCount = data?.data?.requests?.filter((r) => r.status === 'expired').length || 0
  const totalCount = data?.data?.total || 0

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row gutter={16}>
          <Col xs={24} sm={8} md={6}>
            <Card>
              <Statistic
                title="Approved"
                value={approvedCount}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Card>
              <Statistic
                title="Rejected"
                value={rejectedCount}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Card>
              <Statistic
                title="Expired"
                value={expiredCount}
                valueStyle={{ color: '#8c8c8c' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={24} md={6}>
            <Card>
              <Statistic title="Total Processed" value={totalCount} />
            </Card>
          </Col>
        </Row>

        <Card
          title="Approval History"
          extra={
            <Space>
              <Select
                style={{ width: 120 }}
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { label: 'Approved', value: 'approved' },
                  { label: 'Rejected', value: 'rejected' },
                  { label: 'Expired', value: 'expired' },
                ]}
              />
              <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                Refresh
              </Button>
            </Space>
          }
        >
          <Table
            columns={columns}
            dataSource={data?.data?.requests || []}
            rowKey="id"
            loading={isLoading}
            pagination={{
              total: data?.data?.total || 0,
              pageSize: 50,
              showSizeChanger: false,
              showTotal: (total) => `Total ${total} requests`,
            }}
            scroll={{ x: 'max-content' }}
            expandable={{
              expandedRowRender: (record) => (
                <Descriptions column={1} size="small" bordered>
                  {record.reason && (
                    <Descriptions.Item label="Request Reason">{record.reason}</Descriptions.Item>
                  )}
                  {record.rejected_reason && (
                    <Descriptions.Item label="Rejection Reason">
                      {record.rejected_reason}
                    </Descriptions.Item>
                  )}
                  {record.approved_at && (
                    <Descriptions.Item label="Processed At">
                      {dayjs(record.approved_at).format('MMM D, YYYY HH:mm:ss')}
                    </Descriptions.Item>
                  )}
                  {record.created_at && (
                    <Descriptions.Item label="Requested At">
                      {dayjs(record.created_at).format('MMM D, YYYY HH:mm:ss')}
                    </Descriptions.Item>
                  )}
                  {record.expires_at && (
                    <Descriptions.Item label="Expired At">
                      {dayjs(record.expires_at).format('MMM D, YYYY HH:mm:ss')}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
              rowExpandable: (record) =>
                !!(
                  record.reason ||
                  record.rejected_reason ||
                  record.approved_at ||
                  record.expires_at
                ),
            }}
          />
        </Card>
      </Space>
    </div>
  )
}
